import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { Observable, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, filter } from "rxjs/operators";
import { BaseItem } from "src/app/pages/base-item/models/base-item.model";
import { BrandsService } from "src/app/pages/brands/services/brands.service";
import { Category } from "src/app/pages/categories/models/category.model";
import { CategoriesService } from "src/app/pages/categories/services/categories.service";
import { CollectionService } from "src/app/pages/collections/services/collection.service";
import { EmailPromotionsService } from "src/app/pages/email-promotions/services/email-promotions.service";
import { TargetType } from "src/app/pages/main-screen/models/main-screen.model";
import { ProductsService } from "src/app/pages/products/services/products.service";
import { SubSink } from "subsink";
import { GenerateLinkService } from "../../services/generate-link.service";

@Component({
  selector: "app-generate-link",
  templateUrl: "./generate-link.component.html",
  styleUrls: ["./generate-link.component.scss"],
})
export class GenerateLinkComponent implements OnInit, OnDestroy {
  private subs = new SubSink();
  formGroup: UntypedFormGroup;
  isLoading$: Observable<boolean>;
  selected = 0;
  targetType: TargetType = 0;
  searchInput$ = new Subject<string>();
  productSearchInput$ = new Subject<string>();
  brandSearchInput$ = new Subject<string>();
  categorySearchInput$ = new Subject<string>();
  collectionSearchInput$ = new Subject<string>();

  selectedCategory: Category;

  constructor(
    private toasterService: ToastrService,
    private generateLinkService: GenerateLinkService,
    private modal: NgbActiveModal,
    private emailPromotionsService: EmailPromotionsService,
    public collectionService: CollectionService,
    public categoriesService: CategoriesService,
    public productsService: ProductsService,
    public brandsService: BrandsService,
    private fb: UntypedFormBuilder,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.isLoading$ = this.emailPromotionsService.isLoading$;

    this.loadCategories();
    this.loadProducts();
    this.loadCollections();

    this.collectionService.fetch({ queryParams: { pageSize: 100 } });
    this.categoriesService.fetch({ changeRoute: false });
    this.productsService.fetch({ queryParams: { pageSize: 20 } });
    this.loadForm();
  }

  loadForm() {
    this.formGroup = this.fb.group({
      id: [0],
      productId: null,
      categoryId: null,
      brandId: null,
      collectionId: null,
    });
  }

  reset() {
    this.formGroup.patchValue({
      productId: null,
      categoryId: null,
      brandId: null,
      collectionId: null,
    });
  }

  trackByFn(item: BaseItem) {
    return item.id;
  }

  brandsCategoryFilter(cat) {
    this.brandsService.fetch({ changeRoute: false });
    this.brandsService.patchState(null, { categoryId: cat.id, pageSize: 30 });
  }

  onCancelClicked() {
    this.modal.dismiss();
  }

  onSaveClicked() {
    this.generateLinkService
      .generateLink(this.formGroup.value)
      .subscribe((res) => {
        this.copyLink(res.shortLink);
      });
    this.modal.close(this.formGroup.value);
  }

  copyLink(link) {
    const selBox = document.createElement("textarea");
    selBox.style.position = "fixed";
    selBox.style.left = "0";
    selBox.style.top = "0";
    selBox.style.opacity = "0";
    selBox.value = link;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand("copy");
    document.body.removeChild(selBox);
    if (link) {
      this.toasterService.success("Link has been copied");
    } else {
      this.toasterService.error("Error Occured");
    }
  }

  loadCategories() {
    this.categorySearchInput$
      .pipe(
        filter((res) => {
          return res !== null;
        }),
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe((term) => {
        if (term.length >= 2)
          this.categoriesService.patchState({ searchTerm: term });
        else this.categoriesService.patchState({ searchTerm: "" });
      });
  }

  loadBrands() {
    this.brandSearchInput$
      .pipe(
        filter((res) => {
          return res !== null;
        }),
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe((term) => {
        if (term.length >= 2)
          this.brandsService.patchState({ searchTerm: term });
        else this.brandsService.patchState({ searchTerm: "" });
      });
  }

  loadProducts() {
    this.productSearchInput$
      .pipe(
        filter((res) => {
          return res !== null;
        }),
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe((term) => {
        if (term.length >= 2)
          this.productsService.patchState({ searchTerm: term });
        else this.productsService.patchState({ searchTerm: "" });
      });
  }
  loadCollections() {
    this.collectionSearchInput$
      .pipe(
        filter((res) => {
          return res !== null;
        }),
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe((term) => {
        if (term.length >= 2)
          this.collectionService.patchState({ searchTerm: term });
        else this.collectionService.patchState({ searchTerm: "" });
      });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
